import { CompanyAnalysis } from '../types/geminiTypes';
import { KeeperAssessment, KeeperRequest } from '../types/geminiTypes';
import { analyzeCompany, callGeminiWithTools, cleanJson, extractText } from './geminiService';
import { enqueueAnalysis } from './analysisQueue';
import { MODELS } from '../config/models';
import { db } from '../firebase';
import { collection, query, where, orderBy, limit, getDocs, doc, setDoc, Timestamp } from 'firebase/firestore';

// Shape returned by /api/edgar/insider-signal
interface InsiderSignalData {
  signal: 'CLUSTER_BUY' | 'CLUSTER_SELL' | 'MIXED' | 'SILENT';
  signalRationale: string;
  uniqueBuyers: number;
  uniqueSellers: number;
  totalForm4Count: number;
  lookbackDays: number;
  fetchSuccess: boolean;
  errorNote: string;
}

const STALENESS_THRESHOLD_DAYS = 30;

// ─── STALENESS CHECK ─────────────────────────────────────────────────────────

export function getAnalysisAgeDays(analysisDate: string): number {
  const date = new Date(analysisDate);
  const now = new Date();
  return Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
}

// ─── FETCH MOST RECENT ORACLE ANALYSIS FROM FIRESTORE ────────────────────────

export async function fetchExistingAnalysis(
  ticker: string,
  uid: string
): Promise<{ analysis: CompanyAnalysis; date: string } | null> {
  try {
    const analysesRef = collection(db, 'users', uid, 'analyses');
    const q = query(
      analysesRef,
      where('ticker', '==', ticker.toUpperCase()),
      orderBy('createdAt', 'desc'),
      limit(1)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    const doc = snapshot.docs[0];
    const data = doc.data();
    return {
      analysis: data.analysis as CompanyAnalysis,
      date: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString()
    };
  } catch (err) {
    console.error(`[KEEPER] Firestore fetch failed for ${ticker}:`, err);
    return null;
  }
}

// ─── KEEPER ORCHESTRATION FLOW ───────────────────────────────────────────────

// How the Keeper flow decides which Oracle analysis to feed the agent:
//   'fresh' — always run analyzeCompany() and save a new analysis, regardless of
//             whether one exists or how old it is.
//   'stale' — use the most recent existing analysis regardless of age; if none
//             exists, fall through to a fresh run. stalenessWarning is set when
//             the reused analysis is older than the threshold so the note renders.
//   'auto'  — (default) use an existing analysis that is <= threshold; if it is
//             older, return early WITHOUT calling Gemini (keeperAssessment: null,
//             stalenessWarning: true) so the UI can prompt. If none exists, run fresh.
export type KeeperAnalysisMode = 'auto' | 'fresh' | 'stale';

export interface KeeperFlowResult {
  keeperAssessment: KeeperAssessment | null;   // null only on 'auto' early-return
  oracleAnalysis: CompanyAnalysis | null;
  analysisDate: string | null;
  analysisAgeDays: number | null;
  ranFreshOracle: boolean;
  stalenessWarning: boolean;
  mode: KeeperAnalysisMode;
}

// Fire-and-forget telemetry to the existing /api/telemetry POST (same shape and
// error handling as the pre-existing keeper_analysis_cached event).
function emitKeeperTelemetry(event: string, properties: Record<string, unknown>): void {
  fetch('/api/telemetry', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ event, properties }),
  }).catch(e => console.error(e));
}

export async function runKeeperFlow(
  request: KeeperRequest,
  uid: string,
  onProgress?: (message: string) => void,
  mode: KeeperAnalysisMode = 'auto'
): Promise<KeeperFlowResult> {

  const tickerUpper = request.ticker.toUpperCase();
  emitKeeperTelemetry('keeper_flow_started', { ticker: tickerUpper, mode });

  // Track the last progress message so a mid-pipeline failure can report the
  // stage it died at (cheap, deterministic — see keeper_oracle_failed below).
  let lastStage = '';
  const progress = (message: string) => { lastStage = message; onProgress?.(message); };

  // Step 1: Check for existing analysis
  progress("Checking for existing Oracle analysis...");
  const existing = await fetchExistingAnalysis(request.ticker, uid);
  const existingAgeDays = existing ? getAnalysisAgeDays(existing.date) : null;
  const existingIsStale =
    existingAgeDays !== null && existingAgeDays > STALENESS_THRESHOLD_DAYS;

  // Step 2: Resolve which Oracle analysis feeds the Keeper, per mode. The only
  // path that reaches analyzeCompany() is `runFresh`; 'auto' on a stale analysis
  // returns early here so no Gemini Keeper call is spent before the user chooses.
  let runFresh: boolean;
  switch (mode) {
    case 'fresh':
      runFresh = true;
      break;
    case 'stale':
      runFresh = !existing;   // reuse any existing analysis; fresh only if none
      break;
    case 'auto':
    default:
      if (existing && existingIsStale) {
        emitKeeperTelemetry('keeper_staleness_prompted', {
          ticker: tickerUpper,
          analysisAgeDays: existingAgeDays,
        });
        return {
          keeperAssessment: null,
          oracleAnalysis: existing.analysis,
          analysisDate: existing.date,
          analysisAgeDays: existingAgeDays,
          ranFreshOracle: false,
          stalenessWarning: true,
          mode,
        };
      }
      runFresh = !existing;
      break;
  }

  let oracleAnalysis: CompanyAnalysis;
  let analysisDate: string;
  let analysisAgeDays: number;
  let ranFreshOracle: boolean;
  let stalenessWarning: boolean;

  if (runFresh) {
    progress(existing
      ? `Running a fresh Oracle analysis for ${request.ticker}...`
      : `No existing analysis found for ${request.ticker} — running Oracle analysis first...`);
    // Serialize the Oracle pipeline: at most one analyzeCompany() per browser
    // session at a time, FIFO, deduped by ticker (see analysisQueue). The Keeper
    // agent call below is a single call and runs outside the queue.
    try {
      oracleAnalysis = await enqueueAnalysis(
        request.ticker,
        () => {
          const now = new Date();
          const currentDateTime = `${now.toUTCString()} (User Local Time: ${now.toLocaleString()})`;
          return analyzeCompany(request.ticker, currentDateTime, progress);
        },
        progress
      );
    } catch (err: any) {
      emitKeeperTelemetry('keeper_oracle_failed', {
        ticker: tickerUpper,
        mode,
        status: err?.status ?? err?.error?.code ?? null,
        code: err?.code ?? null,
        stage: lastStage,
      });
      throw err;
    }
    analysisDate = new Date().toISOString();
    analysisAgeDays = 0;
    ranFreshOracle = true;
    stalenessWarning = false;

    emitKeeperTelemetry('keeper_oracle_fresh', {
      ticker: tickerUpper,
      priorAnalysisAgeDays: existingAgeDays,
      mode,
    });

    // Save the fresh analysis to Firestore
    try {
      const ref = doc(collection(db, 'users', uid, 'analyses'));
      await setDoc(ref, {
        ticker: tickerUpper,
        analysis: oracleAnalysis,
        createdAt: Timestamp.now()
      });
      emitKeeperTelemetry('keeper_analysis_cached', {
        ticker: tickerUpper,
        verdict: oracleAnalysis.oracleFramework?.decision?.value || 'UNKNOWN',
        uid,
      });
    } catch (err) {
      console.warn(`[KEEPER] Failed to save fresh analysis for ${request.ticker}:`, err);
    }
  } else {
    // Reuse the existing analysis (guaranteed non-null on this branch).
    oracleAnalysis = existing!.analysis;
    analysisDate = existing!.date;
    analysisAgeDays = existingAgeDays!;
    ranFreshOracle = false;
    stalenessWarning = existingIsStale;

    emitKeeperTelemetry('keeper_oracle_reused', {
      ticker: tickerUpper,
      analysisAgeDays: existingAgeDays,
      mode,
    });
  }

  // Step 3: Fetch insider signal from EDGAR Form 4 (server-side route)
  progress("Checking SEC insider activity...");
  let insiderSignal: InsiderSignalData = {
    signal: 'SILENT',
    signalRationale: 'Insider data unavailable',
    uniqueBuyers: 0,
    uniqueSellers: 0,
    totalForm4Count: 0,
    lookbackDays: 90,
    fetchSuccess: false,
    errorNote: 'Not yet fetched'
  };
  try {
    const insiderRes = await fetch(
      `/api/edgar/insider-signal?ticker=${encodeURIComponent(request.ticker)}&days=90`
    );
    if (insiderRes.ok) {
      insiderSignal = await insiderRes.json() as InsiderSignalData;
    }
  } catch {
    // Non-blocking — proceed with default SILENT signal
  }

  // Step 4: Run The Keeper against the Oracle analysis
  progress("The Keeper is evaluating your position...");
  const keeperAssessment = await runKeeperAgent(
    request,
    oracleAnalysis,
    analysisDate!,
    insiderSignal
  );

  return {
    keeperAssessment,
    oracleAnalysis,
    analysisDate,
    analysisAgeDays,
    ranFreshOracle,
    stalenessWarning,
    mode,
  };
}

async function runKeeperAgent(
  request: KeeperRequest,
  oracleAnalysis: CompanyAnalysis,
  analysisDate: string,
  insiderSignal: InsiderSignalData
): Promise<KeeperAssessment> {

  const ageDays = getAnalysisAgeDays(analysisDate);

  // Extract Bear IRR from valuationScenarios (persisted in CompanyAnalysis)
  let bearIrrNumeric: number | null = null;
  const bearScenario = oracleAnalysis.valuationScenarios?.find(s => s.scenario === 'Bear');
  if (bearScenario?.irr) {
    const match = bearScenario.irr.match(/([-\d.]+)/);
    if (match) bearIrrNumeric = parseFloat(match[1]);
  }
  const bearIrrDisplay = bearIrrNumeric !== null
    ? `${bearIrrNumeric.toFixed(1)}% (extracted from Oracle scenario table)`
    : 'NOT AVAILABLE — treat as WATCH';

  // CALC-5: Signal 2 growth gap is TypeScript-owned. Extraction order:
  //   1) numeric bearDeclineGap fields persisted by the pipeline (preferred)
  //   2) parse expectedReturnMath.impliedGrowth string + Base valuationScenarios growth string
  // If either side is unavailable → NOT AVAILABLE → Signal 2 = WATCH (data gap, not exit evidence).
  let impliedGrowthPct: number | null = null;
  let baseGrowthPct: number | null = null;

  const bdg = (oracleAnalysis as any)?.assumptionRationales?.bearDeclineGap;
  if (bdg && typeof bdg.impliedGrowth === "number" && bdg.impliedGrowth > -1) {
    impliedGrowthPct = bdg.impliedGrowth * 100;
  }
  if (bdg && typeof bdg.baseGrowth === "number") {
    baseGrowthPct = bdg.baseGrowth * 100;
  }

  if (impliedGrowthPct === null) {
    const igStr = (oracleAnalysis as any)?.expectedReturnMath?.impliedGrowth || "";
    const m = String(igStr).match(/(-?[\d.]+)\s*%/);
    if (m) impliedGrowthPct = parseFloat(m[1]);
  }
  if (baseGrowthPct === null) {
    const baseScenario = oracleAnalysis.valuationScenarios?.find(s => s.scenario === "Base");
    const m = String(baseScenario?.growth || "").match(/(-?[\d.]+)\s*%/);
    if (m) baseGrowthPct = parseFloat(m[1]);
  }

  const growthGapPp =
    impliedGrowthPct !== null && baseGrowthPct !== null
      ? Math.abs(impliedGrowthPct - baseGrowthPct)
      : null;
  const growthGapDisplay =
    growthGapPp !== null
      ? `${growthGapPp.toFixed(1)} percentage points (implied ${impliedGrowthPct!.toFixed(1)}% vs base ${baseGrowthPct!.toFixed(1)}%)`
      : "NOT AVAILABLE";

  const entityType = oracleAnalysis.entityType || 'Standard';
  const isType4 = entityType.includes('Type 4');

  const prompt = `
You are The Keeper — Oracle Insight's portfolio hold/exit advisor. You evaluate 
whether a position the user currently owns should be KEPT, WATCHED, or EXITED.

Your philosophical frame is Warren Buffett's: "If you held this for 100 years, 
would you still want it?" You are hyper-conservative on EXIT. Two or more EXIT 
signals are required before issuing an EXIT verdict. A single deteriorating 
signal is a WATCH, not an EXIT.

CRITICAL RULE: You have NOT been given the user's cost basis. This is intentional.
Your assessment must be entirely forward-looking. Sunk cost is irrelevant to 
whether this business deserves continued ownership. Never ask about or reference 
cost basis, profit/loss, or entry price.

═══════════════════════════════════════════════════════
ORACLE ANALYSIS INPUT (your data source)
═══════════════════════════════════════════════════════
Ticker: ${request.ticker}
Current Price: $${request.currentPrice}
Analysis Date: ${analysisDate} (${ageDays} days ago)
Oracle Verdict: ${oracleAnalysis.oracleFramework?.decision?.value}
Oracle Intrinsic Value: $${oracleAnalysis.oracleFramework?.oracleIntrinsicValue || 0}
Price vs IV: ${((request.currentPrice / (oracleAnalysis.oracleFramework?.oracleIntrinsicValue || 1)) * 100).toFixed(1)}% of IV

FULL ORACLE ANALYSIS:
${JSON.stringify(oracleAnalysis, null, 2)}
═══════════════════════════════════════════════════════

SIGNAL TABLE — Evaluate each of the following 8 signals:

1. BUSINESS QUALITY ASSESSMENT
   Evaluate whether Oracle's qualitative analysis identifies this as a sound long-term
   ownership candidate — independent of current price, Oracle verdict, or valuation.

   READ: Oracle's narrative on competitive position, moat durability, management track 
   record, capital allocation discipline, and any red flags or structural concerns 
   identified in the full Oracle analysis JSON above.

   DO NOT use the Oracle verdict value (FAT PITCH/BUY/AVOID/etc.) or any price/IV 
   comparison for this signal. Those are valuation outputs. This signal evaluates 
   business quality only.

   KEEP:  Wide moat stable or improving, no structural red flags, management credible
          and disciplined. Oracle's qualitative narrative supports long-term ownership
          independent of price.
   WATCH: Narrow moat with some deterioration, OR meaningful risk factors identified
          (competitive threats, margin compression, succession risk), OR management 
          track record mixed. Business viable but requires active monitoring.
   EXIT:  Confirmed fundamental deterioration — moat collapsing, structural decline 
          flagged with HIGH confidence, management credibility broken (e.g. 
          value-destructive acquisitions, accounting concerns), OR business model no 
          longer viable. These are business problems, not price problems.

   Signal name for output: "Business Quality Assessment"
   Oracle verdict (for context only, do not use for this signal): ${oracleAnalysis.oracleFramework?.decision?.value}

2. IMPLIED vs BASE GROWTH GAP
   KEEP threshold:  Gap < 3 percentage points
   WATCH zone:      3–6 percentage points
   EXIT threshold:  Gap > 6 percentage points
   Current value:   ${growthGapDisplay}
   The gap above is pre-computed deterministically — do NOT re-derive it from the Oracle JSON.
   Note: If the gap is NOT AVAILABLE, assign WATCH status — a missing figure is a data gap, not a confirmed exit signal.

3. STRUCTURAL DECLINE GATE
   KEEP threshold:  Not fired (isStructuralDecline: false, or LOW/MEDIUM confidence)
   WATCH zone:      isStructuralDecline: true, MEDIUM confidence
   EXIT threshold:  isStructuralDecline: true, HIGH confidence (confirmed by Agent D)

4. MOAT TRAJECTORY
   KEEP threshold:  Stable or improving moat (Wide → Wide, Narrow → Wide, Narrow → Narrow)
   WATCH zone:      Narrow moat with deteriorating indicators
   EXIT threshold:  Confirmed moat degradation (Wide → Narrow, or Narrow → None)
   Source: Oracle moat assessment and historical context

5. CAPITAL ALLOCATION
   KEEP threshold:  Acceptable — buybacks at or below IV, dividends sustainable
   WATCH zone:      One concerning signal (e.g. buybacks marginally above IV)
   EXIT threshold:  Deteriorating — value-destructive buybacks confirmed, 
                    dividend sustainability at risk, or management credibility broken

6. ROIC vs WACC
   Entity type for this analysis: ${entityType}

   ⚠️ TYPE 4 ENTITY RULE: If entity type is "Type 4", assign status "N/A" for this 
   signal. Set currentValue to "Not applicable (Type 4 financial entity)" and rationale
   to explain that ROIC is structurally distorted by float, reserve liabilities, and 
   investment portfolio drag for financial/insurance conglomerates — ROTCE and P/TBV 
   are the appropriate metrics. Do NOT attempt to evaluate ROIC vs WACC for Type 4.

   FOR ALL OTHER ENTITY TYPES:
   KEEP threshold:  ROIC consistently above cost of capital
   WATCH zone:      ROIC declining toward WACC over last 2 years
   EXIT threshold:  ROIC consistently below WACC for 3+ years

7. BEAR IRR AT CURRENT PRICE
   KEEP threshold:  Bear IRR > 6%
   WATCH zone:      Bear IRR 3–6%
   EXIT threshold:  Bear IRR < 3%
   Current value:   ${bearIrrDisplay}
   Note: If Bear IRR is NOT AVAILABLE, assign WATCH status — a missing figure is a data gap, not a confirmed exit signal.

8. INSIDER CONVICTION (open-market transactions, last 90 days)
   KEEP threshold:  Cluster buying — 2 or more insiders made open-market purchases with no offsetting sales
   WATCH zone:      Mixed signals (buyers and sellers both present) OR data unavailable (treat gap as WATCH)
   EXIT threshold:  Cluster selling — 2 or more insiders made open-market sales with no offsetting purchases
   
   CRITICAL RULES for this signal:
   - Only open-market purchases (SEC code P) and sales (SEC code S) count.
   - Grants (code A), option exercises (code M), and tax withholding sales (code F) are excluded.
   - Absence of transactions (SILENT) is NOT a red flag — assign KEEP for silence.
   - Cluster transactions by multiple insiders carry far more weight than isolated single-insider activity.
   - A single insider buying is encouraging but insufficient for CLUSTER_BUY — requires 2 or more.
   
   Current insider data (last ${insiderSignal.lookbackDays} days):
   Signal classification: ${insiderSignal.signal}
   Open-market buyers: ${insiderSignal.uniqueBuyers} unique insider(s)
   Open-market sellers: ${insiderSignal.uniqueSellers} unique insider(s)
   Form 4 filings reviewed: ${insiderSignal.totalForm4Count}
   Detail: ${insiderSignal.signalRationale}
   ${!insiderSignal.fetchSuccess ? `Data note: EDGAR fetch failed (${insiderSignal.errorNote}) — assign WATCH for this signal.` : ''}

═══════════════════════════════════════════════════════
VERDICT LOGIC (MANDATORY — apply exactly)
═══════════════════════════════════════════════════════
Count EXIT signals and WATCH signals from your evaluation above.

EXIT:  exitSignalCount >= 2
WATCH: exitSignalCount === 1 OR watchSignalCount >= 2  
KEEP:  all other cases (0 EXIT signals AND 0-1 WATCH signals)

This logic is non-negotiable. Do not override it based on qualitative judgment.
If the math says EXIT but you feel the business is great, that tension belongs
in verdictRationale — not in overriding the verdict.

VERDICTRATIONALE FRAMING RULE:
When the verdict is WATCH or EXIT AND Signal 1 (Business Quality Assessment) = KEEP
AND Signal 3 (Structural Decline Gate) = KEEP AND Signal 4 (Moat Trajectory) = KEEP
AND Signal 5 (Capital Allocation) = KEEP — meaning all business quality signals are
favorable and only valuation signal(s) are elevated — your verdictRationale MUST 
lead with a statement acknowledging business quality before stating the valuation 
concern. Example: "This is a fundamentally sound business — the WATCH verdict is 
driven entirely by valuation, not any quality concern." Do NOT lead with the exit 
reason when business quality signals are uniformly KEEP.

═══════════════════════════════════════════════════════
BUFFETT FRAME
═══════════════════════════════════════════════════════
After completing your signal evaluation, answer this question honestly:
"If the user held this position for 100 years, would they still want it?"
This is not a rhetorical device. It is the ultimate business quality test.
A wide-moat compounder trading at 140% of IV should still pass this test.
A structurally declining commodity business at 80% of IV may not.

═══════════════════════════════════════════════════════
OUTPUT FORMAT
═══════════════════════════════════════════════════════
Respond ONLY with valid JSON matching this exact structure. 
Do not wrap in markdown code blocks.

{
  "ticker": "${request.ticker}",
  "companyName": "string",
  "analysisDate": "${analysisDate}",
  "analysisAge": ${ageDays},
  "analysisStale": ${ageDays > STALENESS_THRESHOLD_DAYS},
  "verdict": "KEEP | WATCH | EXIT",
  "verdictRationale": "2-3 sentences explaining the verdict",
  "signals": [
    {
      "signalName": "string",
      "keepThreshold": "string",
      "exitThreshold": "string",
      "currentValue": "string",
      "status": "KEEP | WATCH | EXIT | N/A",
      "rationale": "string"
    }
  ],
  "exitSignalCount": number,
  "watchSignalCount": number,
  "thesisIntact": boolean,
  "thesisIntactRationale": "string",
  "whatToWatch": ["string", "string"],
  "exitTriggers": ["string", "string"],
  "hundredYearQuestion": "string — direct answer to the 100-year hold question",
  "oracleVerdict": "${oracleAnalysis.oracleFramework?.decision?.value}",
  "oracleIV": ${oracleAnalysis.oracleFramework?.oracleIntrinsicValue || 0},
  "currentPrice": ${request.currentPrice},
  "priceVsIV": ${((request.currentPrice / (oracleAnalysis.oracleFramework?.oracleIntrinsicValue || 1)) * 100).toFixed(1)},
  "confidenceNote": "string — any caveats about analysis age, data quality, or confidence penalties"
}

IMPORTANT: Your signals array must contain exactly 8 entries — one for
each signal in the table above, in order. Signal 1 must be named
"Business Quality Assessment". Signal 8 must be named "Insider Conviction"
and must reflect the EDGAR data provided. For Type 4 entities, Signal 6
must have status "N/A" — this does not count toward EXIT or WATCH tallies.
`;

  const response = await callGeminiWithTools({
    model: MODELS.KEEPER.primary,
    // [THINKING-EXPLICIT] Agent E thinking level set explicitly from the registry.
    config: { thinking_level: MODELS.KEEPER.thinkingLevel },
    contents: [{ role: 'user', parts: [{ text: prompt }] }],
    tools: [],
    symbol: request.ticker,
    // [MODEL-1] Keeper runs on the priority tier — pass its entry so buildGeminiConfig applies it.
    entry: MODELS.KEEPER,
  });

  const cleaned = cleanJson(extractText(response));
  const parsed = JSON.parse(cleaned) as KeeperAssessment;

  // Post-processing: enforce verdict logic deterministically
  // The agent should get this right, but we enforce it in code as a hard gate
  // Deterministic Signal 7 override if Bear IRR was parseable
  if (bearIrrNumeric !== null) {
    const signal7 = parsed.signals.find(s => s.signalName?.toLowerCase().includes('bear irr') || s.signalName?.toLowerCase().includes('irr'));
    if (signal7) {
      if (bearIrrNumeric > 6) signal7.status = 'KEEP';
      else if (bearIrrNumeric >= 3) signal7.status = 'WATCH';
      else signal7.status = 'EXIT';
      signal7.currentValue = `Bear IRR: ${bearIrrNumeric.toFixed(1)}%`;
      signal7.rationale = `Bear IRR of ${bearIrrNumeric.toFixed(1)}% sourced from Oracle scenario table. ${signal7.rationale || ''}`.trim();
    }
  }

  // Deterministic Signal 8 override based on insider conviction data
  if (insiderSignal) {
    const signal8 = parsed.signals.find(s =>
      s.signalName?.toLowerCase().includes('insider') ||
      s.signalName?.toLowerCase().includes('conviction')
    );
    if (signal8) {
      if (!insiderSignal.fetchSuccess) {
        signal8.status = 'WATCH'; // Data gap — treat as WATCH
      } else {
        switch (insiderSignal.signal) {
          case 'CLUSTER_BUY':  signal8.status = 'KEEP'; break;
          case 'CLUSTER_SELL': signal8.status = 'EXIT'; break;
          case 'MIXED':        signal8.status = 'WATCH'; break;
          case 'SILENT':
          default:             signal8.status = 'KEEP'; break; // Silence is not a red flag
        }
      }
      signal8.currentValue = `${insiderSignal.uniqueBuyers} buyer(s), ${insiderSignal.uniqueSellers} seller(s) in 90 days`;
    }
  }

  // Deterministic Signal 6 override for Type 4 entities
  if (isType4) {
    const signal6 = parsed.signals.find(s =>
      s.signalName?.toLowerCase().includes('roic') ||
      s.signalName?.toLowerCase().includes('wacc')
    );
    if (signal6) {
      signal6.status = 'N/A';
      signal6.currentValue = 'Not applicable (Type 4 financial entity)';
      signal6.rationale = 'ROIC vs WACC is not a meaningful metric for financial/insurance conglomerates. Reported ROIC is structurally distorted by float, reserve liabilities, and investment portfolio drag. The appropriate metrics for Type 4 entities are ROTCE, NIM, and P/TBV.';
    }
  }

  // Recount after Signal 6/7/8 overrides — N/A signals excluded from both counts
  const exitCountFinal = parsed.signals.filter(s => s.status === 'EXIT').length;
  const watchCountFinal = parsed.signals.filter(s => s.status === 'WATCH').length;
  parsed.exitSignalCount = exitCountFinal;
  parsed.watchSignalCount = watchCountFinal;

  // Deterministic verdict override — agent cannot override this
  if (exitCountFinal >= 2) {
    parsed.verdict = 'EXIT';
  } else if (exitCountFinal === 1 || watchCountFinal >= 2) {
    parsed.verdict = 'WATCH';
  } else {
    parsed.verdict = 'KEEP';
  }

  const oracleDecision = oracleAnalysis.oracleFramework?.decision?.value;

  if (parsed.verdict === 'EXIT' && (oracleDecision === 'BUY' || oracleDecision === 'FAT PITCH')) {
    parsed.verdict = 'WATCH';
    parsed.verdictRationale = `[Consistency Gate] Keeper verdict downgraded EXIT → WATCH. Oracle rates this ${oracleDecision} — the current price still offers margin of safety. Exit signals are elevated risks to monitor, not an exit trigger while valuation supports ownership. Original rationale: ${parsed.verdictRationale}`;
  }

  if (parsed.verdict === 'KEEP' && oracleDecision === 'AVOID') {
    parsed.verdict = 'WATCH';
    parsed.verdictRationale = `[Consistency Gate] Keeper verdict upgraded KEEP → WATCH. Oracle rates this AVOID — an unqualified KEEP is inconsistent with Oracle's valuation conclusion. Monitor for valuation reset or exit opportunity. Original rationale: ${parsed.verdictRationale}`;
  }

  return parsed;
}
